namespace $ {

	type $shm_ba_mcp_client_request = {
		type: 'request'
		id: string
		method: string
		params?: Record< string, any >
	}

	export class $shm_ba_mcp_client extends $mol_object {

		private socket: WebSocket | null = null
		private reconnecting = false
		private registering = false

		dashboard() {
			return null as any as $.$$.$shm_ba_dashboard
		}

		url() {
			const url = this.$.$mol_state_local.value< string >( 'hitalama_ba_mcp_url' ) ?? this.url_default()
			const token = this.$.$shm_ba_pb.user_agent_token()
			if( !token ) return url

			const parsed = new URL( url, this.$.$mol_dom_context.document?.location.href )
			parsed.searchParams.set( 'token', token )
			return parsed.toString()
		}

		url_default() {
			const location = this.$.$mol_dom_context.document?.location
			if( location?.protocol === 'https:' && location.hostname.startsWith( 'dashboard.' ) ) {
				return `wss://ba-mcp.${ location.hostname.slice( 10 ) }/hitalama-ba-mcp`
			}
			return 'ws://127.0.0.1:4818/hitalama-ba-mcp'
		}

		client_id( next?: string ) {
			const key = '$' + 'hitalama_ba_mcp_client.client_id'
			const stored = this.$.$mol_state_session.value< string >( key, next )
			if( stored ) return stored
			const id = `ba_tab_${ Date.now().toString( 36 ) }_${ Math.random().toString( 36 ).slice( 2 ) }`
			this.$.$mol_state_session.value( key, id )
			return id
		}

		auto() {
			this.connect()
			this.register_later()
			return null
		}

		connect() {
			if( typeof WebSocket === 'undefined' ) return
			if( this.socket?.readyState === WebSocket.OPEN ) return
			if( this.socket?.readyState === WebSocket.CONNECTING ) return

			const socket = this.socket = new WebSocket( this.url() )
			socket.onopen = ()=> this.register()
			socket.onmessage = event => $mol_wire_async( this ).message( event )
			socket.onclose = ()=> this.reconnect_later()
			socket.onerror = ()=> socket.close()
		}

		reconnect_later() {
			this.socket = null
			if( this.reconnecting ) return
			this.reconnecting = true
			new this.$.$mol_after_timeout( 1500, ()=> {
				this.reconnecting = false
				this.connect()
			} )
		}

		register_later() {
			if( this.registering ) return
			this.registering = true
			new this.$.$mol_after_timeout( 2000, ()=> {
				this.registering = false
				this.register()
				this.register_later()
			} )
		}

		send( message: Record< string, any > ) {
			const socket = this.socket
			if( !socket || socket.readyState !== WebSocket.OPEN ) return
			socket.send( JSON.stringify( message ) )
		}

		register() {
			this.send({
				type: 'register',
				... this.client_info(),
			})
		}

		client_info() {
			const dashboard = this.dashboard()
			const report = this.safe_read( ()=> dashboard.report_current_model(), null )
			return {
				client_id: this.client_id(),
				dashboard_id: this.safe_read( ()=> dashboard.dashboard_model()?.id() ?? '', '' ),
				dashboard_title: this.safe_read( ()=> dashboard.dashboard_title(), '' ),
				report_id: report?.id() ?? '',
				report_title: report?.title() ?? '',
				url: this.$.$mol_dom_context.document?.location.href ?? '',
				focused: this.$.$mol_dom_context.document?.hasFocus?.() ?? false,
				visible: this.$.$mol_dom_context.document?.visibilityState !== 'hidden',
				last_seen: Date.now(),
			}
		}

		message( event: MessageEvent ) {
			const request = JSON.parse( String( event.data ) ) as $shm_ba_mcp_client_request
			if( request.type !== 'request' ) return
			this.answer( request )
		}

		async answer( request: $shm_ba_mcp_client_request ) {
			try {
				const result = await this.$.$mol_wire_async( this ).handle( request.method, request.params ?? {} )
				this.send({
					type: 'response',
					id: request.id,
					result,
				})
			} catch( error ) {
				this.send({
					type: 'response',
					id: request.id,
					error: {
						message: error instanceof Error ? error.message : String( error ),
					},
				})
			}
		}

		async handle( method: string, params: Record< string, any > ) {
			if( method === 'dashboard_context' ) return this.dashboard_context()
			if( method === 'report_list' ) return this.report_list()
			if( method === 'report_create' ) return this.report_create( String( params.title ?? '' ), String( params.code ?? '' ) )
			if( method === 'report_rename' ) return this.report_rename( String( params.report_id ?? '' ), String( params.title ?? '' ) )
			if( method === 'report_delete' ) return this.report_delete( String( params.report_id ?? '' ) )
			if( method === 'report_select' ) return this.report_select( String( params.report_id ?? '' ) )
			if( method === 'report_get_code' ) return this.report_get_code( String( params.report_id ?? '' ) )
			if( method === 'report_set_code' ) return this.report_set_code( String( params.report_id ?? '' ), String( params.code ?? '' ) )
			if( method === 'filter_set' ) return this.filter_set(
				String( params.kind ?? '' ),
				Array.isArray( params.values ) ? params.values.map( String ) : [],
				String( params.mode ?? 'only' ),
			)
			throw new Error( `Unknown BA bridge method "${ method }"` )
		}

		dashboard_context() {
			const dashboard = this.dashboard()
			const report = dashboard.report_current_model()

			const has_source = this.safe_read( ()=> dashboard.has_source(), false )
			const period = this.safe_read( ()=> dashboard.period(), {
				from: '0001-01-01',
				to: '2999-12-31',
			} )
			const runtime_var = String.fromCharCode( 36 )

			return {
				dashboard_id: dashboard.dashboard_model()?.id() ?? '',
				dashboard_title: dashboard.dashboard_title(),
				report_id: report?.id() ?? '',
				report_title: report?.title() ?? '',
				file_uri: this.safe_read( ()=> dashboard.file_uri(), '' ),
				has_source,
				period,
				period_all: this.safe_read( ()=> dashboard.period_all(), period ),
				search_query: this.safe_read( ()=> dashboard.search_query(), '' ),
				search_mode: this.safe_read( ()=> dashboard.search_mode(), 'fts' ),
				chart_metric: this.safe_read( ()=> dashboard.chart_metric(), 'Сообщений' ),
				chart_group: this.safe_read( ()=> dashboard.chart_group(), 'day' ),
				tags_filter: this.safe_read( ()=> dashboard.tags_filter(), {} ),
				authors_filter: this.safe_read( ()=> dashboard.authors_filter(), {} ),
				sources_filter: this.safe_read( ()=> dashboard.sources_filter(), {} ),
				runtime: {
					style: 'plain JavaScript snippet body',
					return: 'Return a $mol view/value from the top level, for example: return ' + runtime_var + 'make( $.$mol_view, { sub: ()=> [ ... ] } )',
					helpers: [
						'$',
						runtime_var + 'make( View, config )',
						runtime_var + 'view_data( key, fallback? )',
						runtime_var + 'dashboard',
						runtime_var + 'report',
						runtime_var + 'filters',
						runtime_var + 'tables',
						runtime_var + 'query( sqlText )',
						runtime_var + 'sql`...`',
						runtime_var + 'sql.text( value )',
						runtime_var + 'sql.ident( name )',
						runtime_var + 'summary()',
						runtime_var + 'posts()',
						runtime_var + 'timeseries()',
						runtime_var + 'tags()',
						runtime_var + 'authors()',
						runtime_var + 'sources()',
					],
					data_shapes: {
						[ runtime_var + 'summary' ]: '{ head: string[], rows: string[][] }',
						[ runtime_var + 'posts' ]: '{ head: string[], rows: string[][] }',
						[ runtime_var + 'query' ]: '{ head: string[], rows: string[][] }',
						[ runtime_var + 'tables' ]: '{ messages: string, source: string, filtered: string, tags: string }',
						[ runtime_var + 'timeseries' ]: '{ date: string, value: number }[]',
						[ runtime_var + 'tags' ]: '{ head: string[], rows: string[][] }',
						[ runtime_var + 'authors' ]: '{ head: string[], rows: string[][] }',
						[ runtime_var + 'sources' ]: '{ head: string[], rows: string[][] }',
					},
				},
			}
		}

		filter_set( kind: string, values: string[], mode: string ) {
			const dashboard = this.dashboard()
			const filter = this.filter_make( values, mode )

			if( kind === 'tags' ) dashboard.tags_filter( filter )
			else if( kind === 'authors' ) dashboard.authors_filter( filter )
			else if( kind === 'sources' ) dashboard.sources_filter( filter )
			else throw new Error( `Unknown BA filter kind "${ kind }"` )

			this.register()
			return {
				kind,
				mode,
				values,
				filter,
			}
		}

		filter_make( values: string[], mode: string ) {
			const clean = values.map( value => value.trim() ).filter( Boolean )
			if( mode === 'clear' ) return {}
			if( mode === 'exclude' ) return Object.fromEntries( clean.map( value => [ value, 'exclude' ] ) ) as $shm_ba_data_filter
			if( mode === 'off' ) return Object.fromEntries( clean.map( value => [ value, false ] ) ) as $shm_ba_data_filter
			return Object.fromEntries( clean.map( value => [ value, true ] ) ) as $shm_ba_data_filter
		}

		safe_read< Type >( read: ()=> Type, fallback: Type ) {
			const result = this.$.$mol_error_fence(
				read,
				()=> fallback,
				()=> fallback,
			)
			if( this.$.$mol_promise_like( result ) ) return fallback
			return result
		}

		report_list() {
			const current = this.dashboard().report_id()
			return this.dashboard().reports().map( report => ({
				report_id: report.id(),
				title: report.title(),
				current: report.id() === current,
				builtin: report.id() === 'default',
				has_code: report.id() !== 'default' && Boolean( report.js_code().trim() ),
			}) )
		}

		report_create( title: string, code: string ) {
			const dashboard = this.dashboard().dashboard_model()
			if( !dashboard || dashboard.id() === 'default' ) throw new Error( 'Cannot create report for default dashboard' )
			const report = this.$.$shm_ba_report.make_report( dashboard.id(), title || undefined, code )
			this.dashboard().report_id( report.id() )
			this.register()
			return {
				report_id: report.id(),
				title: report.title(),
				code: report.js_code(),
			}
		}

		report_rename( report_id: string, title: string ) {
			if( !title.trim() ) throw new Error( 'Report title is required' )
			const report = this.report_custom( report_id )
			report.rename( title )
			this.register()
			return {
				report_id: report.id(),
				title,
			}
		}

		report_delete( report_id: string ) {
			const report = this.report_custom( report_id )
			const id = report.id()
			report.delete()
			if( this.$.$mol_state_arg.value( 'report' ) === id ) this.dashboard().report_id( 'default' )
			this.register()
			return {
				report_id: id,
				deleted: true,
			}
		}

		report_select( report_id: string ) {
			const report = this.report_by_id( report_id )
			this.dashboard().report_id( report.id() )
			this.register()
			return {
				report_id: report.id(),
				title: report.title(),
				current: true,
			}
		}

		report_get_code( report_id: string ) {
			const report = this.report_by_id( report_id )
			if( report.id() === 'default' ) return {
				report_id: report.id(),
				title: report.title(),
				builtin: true,
				code: null,
			}
			return {
				report_id: report.id(),
				title: report.title(),
				builtin: false,
				code: report.js_code(),
			}
		}

		report_set_code( report_id: string, code: string ) {
			const report = this.report_custom( report_id )
			report.js_code( code )
			this.register()
			return {
				report_id: report.id(),
				title: report.title(),
				builtin: false,
				code,
			}
		}

		report_by_id( report_id: string ) {
			const id = report_id || this.dashboard().report_id()
			const report = this.dashboard().reports().find( report => report.id() === id )
			if( !report ) throw new Error( `Report "${ id }" not found` )
			return report
		}

		report_custom( report_id: string ) {
			const report = this.report_by_id( report_id )
			if( report.id() === 'default' ) throw new Error( 'Cannot modify built-in summary report' )
			return report
		}

		override destructor() {
			const socket = this.socket
			if( socket ) {
				socket.onopen = null
				socket.onmessage = null
				socket.onclose = null
				socket.onerror = null
				socket.close()
			}
			this.socket = null
			super.destructor()
		}

	}

}
